import { useState, useEffect } from "react";
import { Plus, Trash2, Save, X, Check, Loader2 } from "lucide-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import RichTextEditor from "../ui/RichTextEditor";
import { updateFieldContent } from "../../services/consultationform";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

import { FormField } from "../../types/forms";

interface DisclaimerSectionProps {
  userId: string;
  templateId: string;
  isEditable?: boolean;
  fields?: FormField[];
}

interface DisclaimerField {
  id: string;
  label: string;
  checked: boolean;
}

export default function DisclaimerSection({
  userId,
  templateId,
  isEditable = false,
  fields,
}: DisclaimerSectionProps) {
  const storageKey = `${userId}_${templateId}_disclaimerfields`;

  const [storageFields, setFields] = useLocalStorage<DisclaimerField[]>(storageKey, [
    {
      id: crypto.randomUUID(),
      label: "I confirm that I have read and fully understood the above disclaimer.",
      checked: false,
    },
    {
      id: crypto.randomUUID(),
      label:
        "I acknowledge that the salon/service provider is not liable for any reactions or results arising from disclosed conditions.",
      checked: false,
    },
    {
      id: crypto.randomUUID(),
      label:
        "I give my consent to proceed with the treatment/service under the provided terms.",
      checked: false,
    },
  ]);

  const [availableFields] = useState<string[]>([
    "I confirm that I have read the disclaimer.",
    "I understand all possible outcomes of the service.",
    "I have disclosed all medical or allergic conditions.",
    "I accept the terms and conditions of this service.",
    "I agree that the salon is not responsible for undisclosed issues.",
  ]);

  const [customInput, setCustomInput] = useState("");
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [tempContent, setTempContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" | "" }>({
    text: "",
    type: "",
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (message.text) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => setMessage({ text: "", type: "" }), 300);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleToggleCheck = (id: string) => {
    if (!isEditable) {
      setFields(storageFields.map((f) => (f.id === id ? { ...f, checked: !f.checked } : f)));
    }
  };

  const handleAddField = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) {
      setMessage({ text: "Please enter a valid acknowledgment.", type: "error" });
      return;
    }

    if (storageFields.some((f) => f.label === trimmed)) {
      setMessage({ text: "This acknowledgment is already added.", type: "error" });
      return;
    }

    setFields([...storageFields, { id: crypto.randomUUID(), label: trimmed, checked: false }]);
    setCustomInput("");
  };

  const handleDeleteField = (id: string) => {
    setFields(storageFields.filter((f) => f.id !== id));
  };

  // Sync with props but allow local updates
  const [localFields, setLocalFields] = useState<any[]>([]);

  useEffect(() => {
    if (fields && fields.length > 0) {
      setLocalFields(fields.map(f => ({
        id: String(f.id),
        label: f.options?.content || f.label,
        checked: false
      })));
    } else {
      setLocalFields(storageFields);
    }
  }, [fields, storageFields]);

  const displayFields = localFields;

  const handleStartEdit = (field: any) => {
    if (!fields) return;
    setEditingFieldId(field.id);
    setTempContent(field.label);
  };

  const handleSaveEdit = async (fieldId: string) => {
    setIsSaving(true);
    try {
      // Optimistic update
      setLocalFields(prev => prev.map(f =>
        f.id === fieldId ? { ...f, label: tempContent } : f
      ));

      await updateFieldContent(fieldId, tempContent);
      setEditingFieldId(null);
      setMessage({ text: "Updated successfully", type: "success" });
    } catch (err) {
      console.error("Failed to save field content:", err);
      setMessage({ text: "Failed to update", type: "error" });
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <div className="space-y-4">
      <h2 className="text-[15px] font-semibold text-gray-800 mb-2">
        Client Disclaimer
      </h2>
      <div className="space-y-3">
        {displayFields.length > 0 ? (
          displayFields.map((field) => (
            <div key={field.id} className="flex items-start gap-2">

              <div className="flex-1 text-sm text-gray-700 leading-snug">
                {editingFieldId === field.id ? (
                  <div className="space-y-2">
                    <RichTextEditor
                      value={tempContent}
                      onChange={setTempContent}
                      rows={4}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingFieldId(null)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                      <button
                        disabled={isSaving}
                        onClick={() => handleSaveEdit(field.id)}
                        className="p-1 text-green-500 hover:text-green-600 disabled:opacity-50"
                      >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => isEditable && fields && handleStartEdit(field)}
                    className={`${isEditable && fields ? "cursor-pointer hover:bg-gray-50 rounded p-1 -m-1 transition-colors" : ""}`}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: field.label }}
                    />
                  </div>
                )}
              </div>
              {isEditable && !fields && (
                <button
                  onClick={() => handleDeleteField(field.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors mt-1"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-400 italic text-center py-2">
            No acknowledgments added yet.
          </p>
        )}
      </div>
      {isEditable && !fields && (
        <div className="border-t border-gray-200 pt-3 space-y-2">
          {message.text && (
            <div
              className={`text-xs font-medium px-2 py-1 rounded-md transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
                } ${message.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
                }`}
            >
              {message.text}
            </div>
          )}

          <label className="block text-xs font-medium text-gray-600">
            Add New Acknowledgment
          </label>

          <div className="flex items-center gap-2">
            <Select onValueChange={(value) => handleAddField(value)}>
              <SelectTrigger className="flex-1 h-9 text-sm border-gray-300 bg-gray-50">
                <SelectValue placeholder="Choose acknowledgment to add..." />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {availableFields.map((label) => (
                  <SelectItem key={label} value={label}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <input
              type="text"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="Or enter custom acknowledgment..."
              className="flex-1 h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
            />

            <button
              onClick={() => handleAddField(customInput)}
              className="p-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-all"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
